// @ts-check
import { gql, useQuery } from '@apollo/client';
import { Trans } from '@lingui/macro';
import { useParams } from 'react-router-dom';
import { Crisp } from 'crisp-sdk-web';
import { useState } from 'react';
import { ArrowsClockwise, List } from '@phosphor-icons/react';
import {
  Box,
  Flex,
  Link,
  Text,
  Menu,
  MenuButton,
  MenuLink,
  MenuList,
  SettingsIcon,
} from '~/design-system';

const BOTTLEBOOKS_LOGO_URL =
  '//res.cloudinary.com/bottlebooks/image/upload/h_30,dpr_2/bottlebooks/bottlebooks/img/bottlebooks_logo_horiz-blue.png';

const HEADER_QUERY = gql`
  query TopNavbarHeader {
    mySession {
      currentWorkspaceId
      workspaces {
        workspaceId
        owner {
          name
          logo {
            publicId
          }
        }
      }
      currentWorkspace {
        workspaceId
        owner {
          name
          logo {
            publicId
          }
        }
      }
    }
  }
`;

function getInitials(name) {
  return (name || 'BB')
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

function MissingLogoIcon({ companyName }) {
  const initials = getInitials(companyName ?? 'BB');
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'nuanced',
        color: 'gray',
      }}
    >
      <Text sx={{ fontSize: 0, fontWeight: 'bold' }}>{initials}</Text>
    </Box>
  );
}

const navLinkStyle = {
  display: 'inline-block',
  padding: '8px 12px',
  textDecoration: 'none',
  fontSize: '14px',
  lineHeight: 1.5,
  whiteSpace: 'nowrap',
  color: '#6b7280',
  flexShrink: 0,
};

function NavLink({ href, to, children, active, onClick }) {
  const style = {
    ...navLinkStyle,
    color: active ? '#2563eb' : '#6b7280',
  };
  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        style={style}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = '#2563eb';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = active ? '#2563eb' : '#6b7280';
        }}
      >
        {children}
      </a>
    );
  }
  if (to) {
    return (
      <Link
        to={to}
        style={style}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = '#2563eb';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = active ? '#2563eb' : '#6b7280';
        }}
      >
        {children}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{
          ...navLinkStyle,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        {children}
      </button>
    );
  }
  return null;
}

export default function TopNavbar() {
  const { workspaceId } = useParams();
  const portfolioHost = import.meta.env.VITE_PORTFOLIO_HOSTNAME || '';

  const { data } = useQuery(HEADER_QUERY);

  const workspace =
    data?.mySession?.currentWorkspace ||
    data?.mySession?.workspaces?.find((w) => w?.workspaceId === workspaceId);

  const accountUrl = workspaceId
    ? `${portfolioHost}/dashboard/app/next/en/${workspaceId}/classic/settings/account`
    : `${portfolioHost}/dashboard`;

  function openHelp() {
    if (typeof Crisp !== 'undefined' && Crisp.chat) {
      Crisp.chat.show();
    }
  }

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = (
    <>
      <NavLink href={`${portfolioHost}/dashboard/0/portfolio`}>
        <Trans>Portfolio</Trans>
      </NavLink>
      <NavLink href={`${portfolioHost}/dashboard/0/productlists`}>
        <Trans>Lists</Trans>
      </NavLink>
      <NavLink href={`${portfolioHost}/dashboard`}>
        <Trans>Requests & Events</Trans>
      </NavLink>
      <NavLink href={`${portfolioHost}/redirects/elabels`}>
        <Trans>E-Labels</Trans>
      </NavLink>
      {workspaceId && (
        <NavLink to={`/${workspaceId}/history/completed`}>
          <Trans>Import history</Trans>
        </NavLink>
      )}
      <NavLink onClick={openHelp}>
        <Trans>Help</Trans>
      </NavLink>
    </>
  );

  return (
    <Box as="header" sx={{ position: 'sticky', top: 0, zIndex: 1000 }}>
      <Flex
        sx={{
          height: 61,
          px: [3, 4],
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f8f8f8',
          borderBottom: '1px solid #e7e7e7',
          gap: 2,
          minWidth: 0,
        }}
      >
        <Link
          href={`${portfolioHost}/dashboard`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            flexShrink: 0,
          }}
        >
          <img
            src={BOTTLEBOOKS_LOGO_URL}
            alt="Bottlebooks"
            height={30}
          />
        </Link>

        <Flex
          sx={{
            flex: 1,
            minWidth: 0,
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Flex
            sx={{
              gap: [1, 3],
              display: ['none', 'flex'],
              flexShrink: 0,
              minWidth: 'max-content',
              alignItems: 'center',
              overflowX: 'auto',
            }}
          >
            {navLinks}
          </Flex>

          <Box
            as="button"
            type="button"
            onClick={() => setMobileMenuOpen((o) => !o)}
            sx={{
              display: ['flex', 'none'],
              alignItems: 'center',
              justifyContent: 'center',
              width: 44,
              height: 44,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: 'gray',
              '&:hover': { color: 'primary' },
            }}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <List size={24} weight="bold" />
          </Box>

        {workspace ? (
          <Menu>
            <MenuButton
              sx={{
                flexShrink: 0,
                p: 0,
                minWidth: 44,
                minHeight: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                border: '1px solid',
                borderColor: 'primary',
                borderRadius: 1,
              }}
            >
              {workspace?.owner?.logo?.publicId ? (
                <img
                  src={`//res.cloudinary.com/bottlebooks/image/upload/h_44,w_54,dpr_2,c_pad,f_jpg/${workspace.owner.logo.publicId}`}
                  alt={workspace.owner?.name}
                  style={{
                    height: 40,
                    width: 40,
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <MissingLogoIcon companyName={workspace?.owner?.name} />
              )}
            </MenuButton>
            <MenuList sx={{ paddingY: 2 }}>
              <MenuLink href={accountUrl} sx={{ paddingY: 2, paddingX: 3 }}>
                <SettingsIcon sx={{ marginRight: 2 }} />
                <Trans>Account</Trans>
              </MenuLink>
              <MenuLink
                href={`${portfolioHost}/login/selectBrand?redirect_url=/dashboard`}
                sx={{ paddingY: 2, paddingX: 3 }}
              >
                <ArrowsClockwise
                  size={20}
                  style={{ marginRight: 8, verticalAlign: 'middle' }}
                />
                <Trans>Switch business profile</Trans>
              </MenuLink>
            </MenuList>
          </Menu>
        ) : (
          <Box sx={{ width: 44, height: 44 }} />
        )}
        </Flex>
      </Flex>

      {mobileMenuOpen && (
        <Box
          sx={{
            display: ['block', 'none'],
            backgroundColor: '#f8f8f8',
            borderBottom: '1px solid #e7e7e7',
            py: 2,
          }}
        >
          <Flex
            sx={{
              flexDirection: 'column',
              px: 4,
              gap: 0,
            }}
          >
            <MobileNavLink
              href={`${portfolioHost}/dashboard/0/portfolio`}
              onNavigate={() => setMobileMenuOpen(false)}
            >
              <Trans>Portfolio</Trans>
            </MobileNavLink>
            <MobileNavLink
              href={`${portfolioHost}/dashboard/0/productlists`}
              onNavigate={() => setMobileMenuOpen(false)}
            >
              <Trans>Lists</Trans>
            </MobileNavLink>
            <MobileNavLink
              href={`${portfolioHost}/dashboard`}
              onNavigate={() => setMobileMenuOpen(false)}
            >
              <Trans>Requests & Events</Trans>
            </MobileNavLink>
            <MobileNavLink
              href={`${portfolioHost}/redirects/elabels`}
              onNavigate={() => setMobileMenuOpen(false)}
            >
              <Trans>E-Labels</Trans>
            </MobileNavLink>
            {workspaceId && (
              <MobileNavLink
                to={`/${workspaceId}/history/completed`}
                onNavigate={() => setMobileMenuOpen(false)}
              >
                <Trans>Import history</Trans>
              </MobileNavLink>
            )}
            <MobileNavLink onClick={() => { openHelp(); setMobileMenuOpen(false); }}>
              <Trans>Help</Trans>
            </MobileNavLink>
          </Flex>
        </Box>
      )}
    </Box>
  );
}

function MobileNavLink({ href, to, children, active, onClick, onNavigate }) {
  const style = {
    display: 'block',
    padding: '12px 16px',
    textDecoration: 'none',
    fontSize: '14px',
    color: active ? '#2563eb' : '#374151',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    width: '100%',
    textAlign: 'left',
  };
  const handleClick = () => {
    onNavigate?.();
  };
  if (href) {
    return (
      <a href={href} style={style} onClick={handleClick}>
        {children}
      </a>
    );
  }
  if (to) {
    return (
      <Link to={to} style={style} onClick={handleClick}>
        {children}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" style={style} onClick={() => { onClick(); handleClick(); }}>
        {children}
      </button>
    );
  }
  return null;
}
